import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/layout/Navigation';
import SEO from '../components/SEO';
import Footer from '../components/layout/Footer';
import { useLanguage } from '../contexts/LanguageContext';
import QuickLinksBar from '../components/layout/QuickLinksBar';
import { Button } from '../components/ui/button';
import { ArrowRight, Check, X, Minus } from 'lucide-react';
import { motion } from 'framer-motion';

export default function DifferencePage() {
  const { t, language } = useLanguage();

  const comparisons = [
    { row: t.difference.vs.row1, ofa: true, tutoring: false, intSchool: true, langSchool: false },
    { row: t.difference.vs.row2, ofa: true, tutoring: false, intSchool: true, langSchool: false },
    { row: t.difference.vs.row3, ofa: true, tutoring: 'partial', intSchool: false, langSchool: true },
    { row: t.difference.vs.row4, ofa: true, tutoring: true, intSchool: false, langSchool: true },
    { row: t.difference.vs.row5, ofa: true, tutoring: true, intSchool: false, langSchool: true },
    { row: t.difference.vs.row6, ofa: true, tutoring: false, intSchool: true, langSchool: false },
  ];

  const pillars = [
    { title: t.difference.pillars.p1, desc: t.difference.pillars.p1Desc, color: '#2D78F6' },
    { title: t.difference.pillars.p2, desc: t.difference.pillars.p2Desc, color: '#66CA9D' },
    { title: t.difference.pillars.p3, desc: t.difference.pillars.p3Desc, color: '#F8CD46' },
    { title: t.difference.pillars.p4, desc: t.difference.pillars.p4Desc, color: '#926EF7' },
  ];

  const getIcon = (value) => {
    if (value === true) return <Check className="w-5 h-5 text-[#66CA9D]" />;
    if (value === false) return <X className="w-5 h-5 text-[#EA5658]" />;
    return <Minus className="w-5 h-5 text-[#F8CD46]" />;
  };

  return (
    <div data-testid="difference-page" className="min-h-screen bg-white">
      <SEO path="/difference" />
      <Navigation />
      
      {/* Hero */}
      <section className="pt-32 pb-20 bg-[#2D78F6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight tracking-tight mb-6"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {t.difference.heroTitle}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-lg text-white/80 max-w-2xl"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {t.difference.heroSubtitle}
          </motion.p>
        </div>
      </section>

      <QuickLinksBar />

      {/* Intro SEO */}
      <section className="py-12 bg-[#E7EAEF]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[#14274B] leading-relaxed mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {language === 'fr'
              ? 'OFA France n\'est pas un cours d\'anglais, ni un soutien scolaire, ni une école de langues. C\'est un véritable parcours académique structuré, accrédité par COGNIA, qui mène à l\'obtention d\'un U.S. High School Diploma reconnu dans le monde entier. Ce diplôme ouvre les portes des universités américaines et internationales, en complément du Baccalauréat français.'
              : 'OFA France is not an English class, tutoring service, or language school. It\'s a truly structured academic pathway, accredited by COGNIA, leading to a U.S. High School Diploma recognized worldwide. This diploma opens the doors to American and international universities, alongside the French Baccalauréat.'}
          </p>
          <p className="text-[#475569] leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {language === 'fr'
              ? 'Contrairement aux écoles internationales traditionnelles qui nécessitent un changement complet de système scolaire, OFA France s\'intègre en parallèle de l\'école française. Votre enfant conserve sa scolarité au sein de l\'Éducation nationale tout en suivant un programme américain certifié. Les classes sont limitées à 4-8 élèves avec des professeurs natifs américains certifiés.'
              : 'Unlike traditional international schools that require a complete change of school system, OFA France integrates alongside French schooling. Your child maintains their education within the French national system while following a certified American program. Classes are limited to 4-8 students with certified native American teachers.'}
          </p>
        </div>
      </section>

      {/* Valorisation Text */}
      <section className="py-10 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="text-2xl md:text-3xl font-light text-[#14274B] mb-6"
                style={{ fontFamily: 'Gabarito, sans-serif' }}
              >
                {language === 'fr'
                  ? 'Plus qu\'un complément scolaire : un véritable parcours bilingue et international de la Grande Section à la Terminale'
                  : 'More than a school supplement: a genuine bilingual and international pathway from Kindergarten to 12th grade'}
              </motion.h2>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="space-y-4 text-[#475569] text-base leading-relaxed"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                <p>
                  {language === 'fr'
                    ? 'Contrairement aux plateformes de soutien ou aux écoles de langues, OFA France propose un parcours structuré qui accompagne les élèves dans la durée, en parallèle de leur scolarité française.'
                    : 'Unlike tutoring platforms or language schools, OFA France offers a structured pathway that supports students over time, alongside their French schooling.'}
                </p>
                <p>
                  {language === 'fr'
                    ? 'Notre ambition ne se limite pas à apprendre l\'anglais. Le bilinguisme réel repose sur la maîtrise écrite et orale d\'une langue, mais aussi sur la compréhension des cultures, des références et des façons de penser qui l\'accompagnent.'
                    : 'Our ambition goes beyond teaching English. True bilingualism relies on written and oral mastery of a language, but also on understanding the cultures, references and ways of thinking that accompany it.'}
                </p>
                <p>
                  {language === 'fr'
                    ? 'À travers de petites classes et un enseignement exigeant et humain, les élèves développent progressivement des compétences académiques, linguistiques et culturelles solides, essentielles pour évoluer dans un monde international.'
                    : 'Through small classes and demanding yet human teaching, students progressively develop strong academic, linguistic and cultural skills, essential for thriving in an international world.'}
                </p>
              </motion.div>
            </div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="aspect-[4/3] overflow-hidden"
            >
              <img
                src="https://images.pexels.com/photos/34113540/pexels-photo-34113540.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt={language === 'fr' ? 'Tour Eiffel et Statue de la Liberté - Paris' : 'Eiffel Tower and Statue of Liberty - Paris'}
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-3xl md:text-4xl font-light text-[#14274B] mb-12"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {t.difference.vs.title}
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] table-fixed">
              <thead>
                <tr className="border-b-2 border-[#14274B]">
                  <th className="py-4 text-left font-medium text-[#475569] w-[36%]" style={{ fontFamily: 'Poppins, sans-serif' }}></th>
                  <th className="py-4 text-center font-semibold text-[#2D78F6] w-[16%]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {t.difference.vs.ofa}
                  </th>
                  <th className="py-4 text-center font-medium text-[#475569] w-[16%]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {t.difference.vs.tutoring}
                  </th>
                  <th className="py-4 text-center font-medium text-[#475569] w-[16%]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {t.difference.vs.intSchool}
                  </th>
                  <th className="py-4 text-center font-medium text-[#475569] w-[16%]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {t.difference.vs.langSchool}
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisons.map((row, index) => (
                  <tr key={index} className="border-b border-[#E7EAEF]">
                    <td className="py-4 text-[#14274B] font-medium" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {row.row}
                    </td>
                    <td className="py-4"><div className="flex justify-center">{getIcon(row.ofa)}</div></td>
                    <td className="py-4"><div className="flex justify-center">{getIcon(row.tutoring)}</div></td>
                    <td className="py-4"><div className="flex justify-center">{getIcon(row.intSchool)}</div></td>
                    <td className="py-4"><div className="flex justify-center">{getIcon(row.langSchool)}</div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-20 bg-[#14274B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-3xl md:text-4xl font-light text-white mb-12"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {t.difference.pillars.title}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pillars.map((pillar, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 * index }}
                data-testid={`pillar-${index}`}
                className="bg-white/5 p-8"
              >
                <div
                  className="w-3 h-3 mb-6"
                  style={{ backgroundColor: pillar.color }}
                />
                <h3
                  className="text-xl font-semibold text-white mb-3"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  {pillar.title}
                </h3>
                <p
                  className="text-white/60"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  {pillar.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#2D78F6]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-3xl md:text-4xl font-light text-white mb-6"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {t.cta.title}
          </h2>
          <a href="https://admissions.ofalycee.fr" target="_blank" rel="noopener noreferrer">
            <Button
              data-testid="difference-cta"
              size="lg"
              className="bg-white text-[#14274B] hover:bg-[#14274B] hover:text-white px-8 py-6"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {t.cta.primary}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
